import React from "react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/pro-light-svg-icons";
import { Collapse, IconButton, FormControlLabel, Switch, Typography } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { useTranslation } from "react-i18next";

import type { IResponse } from "App/Modules/Models";
import type { ValidationResponse } from "./Forms";

export type ErrorProps = {
    error: IResponse<any> | Error | ValidationResponse;
};

const NOT_AUTHORIZED_CODE = 403;
const TIMEOUT_CODE = 504;

export const Error = (props: ErrorProps) => {
    const { t } = useTranslation();
    const [shown, setShown] = React.useState(true);
    const [open, setOpen] = React.useState(false);

    let errorMessage = "Error.ErrorLoading";

    if ((props.error as IResponse<void>)?.status === NOT_AUTHORIZED_CODE) {
        errorMessage = "Error.NotAuthorized";
    } else if ((props.error as IResponse<void>)?.status === TIMEOUT_CODE) {
        errorMessage = "Error.Timeout";
    }

    const { config, ...otherErrorProps } = props.error as IResponse<any>;

    const publicError: Error | IResponse<any> = otherErrorProps;

    if (config) {
        const { headers, data, ...otherErrorConfigProps } = config;

        if (headers?.Authorization) {
            delete headers.Authorization;
        }

        if (data?.password) {
            delete data.password;
        }

        if (data?.twoFactorAuthCode) {
            delete data.twoFactorAuthCode;
        }

        // headers = headers();
        publicError.config = { headers, data, ...otherErrorConfigProps };
    }

    const errorTitle =
        (props.error as Error)?.message ??
        publicError?.error?.errors ??
        publicError?.error ??
        publicError?.statusText ??
        "An unexpected error occurred";

    let errorDetails = props.error.toString();
    if (!errorDetails || errorDetails === {}.toString()) {
        try {
            errorDetails = JSON.stringify(publicError, null, 2);
        } catch (e) {
            // eslint-disable-next-line no-console
            console.warn("Error stringifying errorDetails", errorDetails);
        }
    }
    if (errorDetails === "{}") {
        errorDetails = null;
    }

    return (
        !!props.error &&
        (props.error as IResponse<void>)?.status !== 400 && (
            <div>
                <Collapse in={shown}>
                    <Alert
                        severity="error"
                        action={
                            <IconButton
                                aria-label="close"
                                color="inherit"
                                size="small"
                                onClick={() => {
                                    setShown(false);
                                }}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </IconButton>
                        }
                    >
                        <Typography variant="h3">{t(errorMessage)}</Typography>
                        {(props.error as IResponse<void>)?.status !== NOT_AUTHORIZED_CODE && (
                            <>
                                <FormControlLabel
                                    control={<Switch checked={open} onChange={() => setOpen(!open)} />}
                                    label={t("Error.ErrorLoading.Details")}
                                />
                                <Collapse in={open}>
                                    <Typography variant="h4">{errorTitle}</Typography>
                                    {errorDetails && <pre className="styled-pre">{errorDetails}</pre>}
                                </Collapse>
                            </>
                        )}
                    </Alert>
                </Collapse>
            </div>
        )
    );
};
